import { Box } from "@mui/material";
// import AddIcon from "@mui/icons-material/Add";
// import StyledButtonV2 from "../../../../../components/app/StyledButtonV2";
import Flex from "../../../../../components/Flex";
import SideSheetDrawer from "../../../../../components/SidesheetDrawer";
import Text from "../../../../../components/Text";
import useDisclosure, {
  DisclosureHost,
} from "../../../../../hooks/use-disclosure";
import {
  applyDefaultColumnSettings,
  getValueFromObj,
  getXtenantId,
  showToastOnApiCall,
} from "../../../../../utils/utils";
import { ScrollArea } from "../../../../../components/ScrollBar";
import CustomDataGridV2 from "../../../../../components/CustomDataGridV2";
import ActionMenu from "../../../../../components/app/ActionMenu";
import DeleteModal from "../../../../../components/app/DeleteModal";
import PatientSelectScroll from "../../../../../components/PatientSelectScroll";
import { AsyncSelectOption } from "../../../../../components/AsyncSelect";
import { useUpdatePatientFlag } from "../../../../../queries/flags/patientFlags/use-update-patient-flag";
import { useRemovePatientFromFlag } from "../../../../../queries/flags/patientFlags/use-remove-patient-from-flag";
import { useSnackbar } from "../../../../../hooks/useSnackbar";
import { useQueryClient } from "@tanstack/react-query";
import { useEffect, useMemo, useState } from "react";
import { useGetPatientFlagById } from "../../../../../queries/flags/patientFlags/use-get-patient-flag-by-uuid";
import { convertPatientDataToArray } from "./flagsConstant";
import { countObjectItems } from "../../../../../utils/array-utility";
import { NonNullableDate } from "../../../../../utils/date-time-utility";
import UpsertPatientFlag from "./UpsertPatientFlag";

const ViewSingleFlagList = ({
  disclosureHost,
}: {
  disclosureHost: DisclosureHost;
}) => {
  // const [pagination, setPagination] = useState<DGT_Pagination>({
  //   page: 0,
  //   pageSize: DEFAULT_PAGE_SIZE,
  // });

  //to-do: need to add pagination

  const showSnackbar = useSnackbar();
  const queryClient = useQueryClient();
  const { mutateAsync: updatePatientFlag } = useUpdatePatientFlag();
  const { mutateAsync: removePatientFromFlag } = useRemovePatientFromFlag();
  const removePatientDisclosure = useDisclosure();
  const [selectedPatient, setSelectedPatient] =
    useState<AsyncSelectOption | null>(null);

  const {
    data: patientFlagData,
    isLoading: patientFlagDataLoading,
    refetch,
  } = useGetPatientFlagById({
    patientFlagId: disclosureHost.data?.uuid as string,
    xTENANTID: getXtenantId() as string,
  });

  const handlePatientSelect = (patient: AsyncSelectOption | null) => {
    setSelectedPatient(patient);
    if (!patient || !patientFlagData?.data) return;

    const existingPatients = patientFlagData?.data?.patient || {};
    const alreadyExists = Object.keys(existingPatients).includes(patient.value);
    if (alreadyExists) {
      setSelectedPatient(null);
      return;
    }

    const updatedPatients = {
      ...existingPatients,
      [patient.value]: patient.label,
    };

    const payload = {
      patientFlags: {
        uuid: patientFlagData?.data?.uuid,
        patient: updatedPatients,
        flags: patientFlagData?.data?.flags,
      },
      xTENANTID: getXtenantId(),
    };

    showToastOnApiCall({
      apiCall: updatePatientFlag(payload),
      showSnackbar,
      onSave: () => {
        queryClient.invalidateQueries({ queryKey: ["patient-flag-list"] });
        setSelectedPatient(null);
        refetch();
      },
    });
  };

  const existingPatientUuids = useMemo(
    () => Object.keys(patientFlagData?.data?.patient || {}),
    [patientFlagData?.data?.patient]
  );

  const [patientList, setPatientList] = useState<any>();
  useEffect(() => {
    setPatientList(
      convertPatientDataToArray(
        patientFlagData?.data?.patient,
        patientFlagData?.data?.userName,
        patientFlagData?.data?.modified
      ) || []
    );
  }, [patientFlagData]);

  const COLUMNS = applyDefaultColumnSettings<any>([
    {
      field: "uuid",
      flex: 0.6,
      minWidth: 150,
      headerName: "Sr.No",
    },
    {
      field: "patient",
      flex: 0.6,
      minWidth: 150,
      headerName: "Patient Name",
      renderCell: ({ row }) => <Text>{getValueFromObj(row?.patient)}</Text>,
    },
    {
      field: "createdBy",
      flex: 0.5,
      minWidth: 150,
      disableColumnMenu: true,
      headerName: "Created By",
      renderCell: ({ row }) => {
        return <Text>{row?.createdBy}</Text>;
      },
    },
    {
      field: "createdOn",
      flex: 0.4,
      minWidth: 120,
      headerName: "Created Date",
      renderCell: ({ row }) => <Text>{NonNullableDate(row?.createdOn)}</Text>,
    },
    {
      field: "Action",
      width: 90,
      renderCell: ({ row }) => {
        const patientUuid = Object.keys(row?.patient || {})[0];
        return (
          <ActionMenu
            options={[{ value: "remove", label: "Remove Patient" }]}
            onActionClick={(v) => {
              if (v === "remove") {
                removePatientDisclosure.open(patientUuid);
              }
            }}
          />
        );
      },
    },
  ]);
  const upsertFlagDisclosure = useDisclosure();

  return (
    <SideSheetDrawer
      open={disclosureHost.opened}
      onClose={disclosureHost.close}
      title={`Edit ${patientFlagData?.data?.flags ? getValueFromObj(patientFlagData?.data?.flags) : ""} Flagged Patient`}
      w="900px"
    >
      {upsertFlagDisclosure.opened && (
        <UpsertPatientFlag
          disclosureHost={upsertFlagDisclosure}
          refetch={refetch}
        />
      )}
      {removePatientDisclosure.opened && (
        <DeleteModal
          w="380px"
          open={removePatientDisclosure.opened}
          onClose={removePatientDisclosure.close}
          id={removePatientDisclosure.data}
          onConfirmation={(patientUuid) =>
            removePatientFromFlag({
              patientFlagId: disclosureHost.data?.uuid as string,
              patientUuid,
              xTENANTID: getXtenantId(),
            }).then((res) => {
              queryClient.invalidateQueries({
                queryKey: ["patient-flag-list"],
              });
              refetch();
              return res;
            })
          }
          content="Are you sure you want to remove this patient from the flag?"
        />
      )}
      <Flex
        mb={1.8}
        justifyContent={"space-between"}
        alignItems={"center"}
        width={"100%"}
        gap={2}
      >
        <Box sx={{ flex: 1 }}>
          <PatientSelectScroll
            label="Search & Select Patient"
            placeholder="Selection Text"
            value={selectedPatient}
            onChange={handlePatientSelect}
            excludeUuids={existingPatientUuids}
          />
        </Box>
        {/* <StyledButtonV2
          label="Add Patient Flag"
          onClick={() => {
            const formatedData = {
              uuid: patientFlagData?.data?.uuid,
              flag: {
                value: getIdFromObj(patientFlagData?.data?.flags),
                label: getValueFromObj(patientFlagData?.data?.flags),
                color: patientFlagData?.data?.flagColour,
              },
              patients: transformMultiSelectFrontEndVal(
                DEFAULT_VALUE_LABEL_LIST,
                patientFlagData?.data?.patient
              ),
            };
            upsertFlagDisclosure.open(formatedData);
          }}
          startIcon={<AddIcon />}
        /> */}
      </Flex>
      <ScrollArea height={"calc(100vh - 11rem)"}>
        <CustomDataGridV2
          hideFooterPagination={true}
          loading={patientFlagDataLoading}
          rows={patientList ?? []}
          getRowId={(row) => row?.uuid}
          columns={COLUMNS}
          // onPaginationModelChange={setPagination}
          // paginationModel={pagination}
          keepNonExistentRowsSelected
          rowCount={countObjectItems(patientList)}
          sx={{ marginTop: "40px" }}
          rowHeight={65}
          //
        />
      </ScrollArea>
    </SideSheetDrawer>
  );
};

export default ViewSingleFlagList;
